import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useApi } from '../../hooks/useApi';
import { PageWrapper, LoadingSpinner } from '../../components';
import { UserCard, UserStatsCard } from '../../components/admin';
import { UserStats } from '../../types';
import { isStandardApiResponse } from '../../types/api';
import '../../styles/pages/user-management.css';

// UserWithStatsインターフェースを追加（UserCard.tsxと統一）
interface UserStatistics {
  total_storage_mb: number;
  total_files: number;
  total_projects: number;
  total_data_count: number;
  last_activity: string | null;
  subscription: {
    plan_name: string;
    max_storage_mb: number;
    usage_percent: number;
  } | null;
}

interface UserWithStats {
  user_id: number;
  username: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  display_name: string;
  organization: string | null;
  is_active: boolean;
  is_staff: boolean;
  is_superuser: boolean;
  date_joined: string;
  last_login: string | null;
  statistics: UserStatistics;
  limits?: {
    status?: {
      storage?: 'OK' | 'WARNING' | 'CRITICAL';
      projects?: 'OK' | 'WARNING' | 'CRITICAL';
      overall?: 'OK' | 'WARNING' | 'CRITICAL';
    };
    percentages?: {
      storage?: number;
      projects?: number;
    };
  };
}

/**
 * ユーザー管理ページ
 * 管理者専用のユーザー一覧・編集・削除機能
 */
const UserManagementPage: React.FC = () => {
  const { isAuthenticated, isAdmin } = useAuth();
  const { execute: apiCall } = useApi();
  
  const [usersWithStats, setUsersWithStats] = useState<UserWithStats[]>([]);
  const [userStats, setUserStats] = useState<UserStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');

  // ユーザー統計データを統合取得（Analytics API使用）
  const fetchUsersWithStats = useCallback(async (search: string = '') => {
    try {
      setLoading(true);
      
      // Analytics APIから全ユーザー統計データを取得
      const token = localStorage.getItem('authToken');
      const cleanToken = token ? token.replace(/"/g, '') : '';
      
      // 検索パラメータをURLに追加
      const searchParam = search ? `?search=${encodeURIComponent(search)}` : '';
      
      const statsResponse = await apiCall(`/api/analytics/users/stats/${searchParam}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${cleanToken}`
        }
      });
      
      if (isStandardApiResponse(statsResponse) && statsResponse.status === 'success') {
        const userData = (statsResponse.data as UserWithStats[]).sort((a, b) => b.user_id - a.user_id);
        
        setUsersWithStats(userData);
        setTotalCount(userData.length);
        setTotalPages(Math.ceil(userData.length / 10));
        setCurrentPage(1);
        
        // 基本統計情報も更新
        const activeUsers = userData.filter((u: UserWithStats) => u.is_active).length;
        const totalStorage = userData.reduce((sum: number, u: UserWithStats) => sum + (u.statistics?.total_storage_mb || 0), 0);
        const staffUsers = userData.filter((u: UserWithStats) => u.is_staff || u.is_superuser).length;
        
        setUserStats({
          total_users: userData.length,
          active_users: activeUsers,
          staff_users: staffUsers,
          recent_registrations: 0,
          recent_logins: 0,
          total_storage_gb: Math.round(totalStorage / 1024 * 100) / 100,
          avg_storage_per_user: userData.length > 0 ? Math.round(totalStorage / userData.length * 100) / 100 : 0
        });
      } else {
        // 標準形式ではないが、データが配列で直接返される場合の処理
        if (Array.isArray(statsResponse)) {
          const sortedData = (statsResponse as UserWithStats[]).sort((a, b) => b.user_id - a.user_id);
          setUsersWithStats(sortedData);
          setTotalCount(sortedData.length);
          setTotalPages(Math.ceil(sortedData.length / 10));
          setCurrentPage(1);
        } else if (statsResponse && typeof statsResponse === 'object' && 'data' in statsResponse && Array.isArray((statsResponse as any).data)) {
          // dataプロパティに配列が含まれている場合
          const userData = (statsResponse as any).data as UserWithStats[];
          const sortedData = userData.sort((a, b) => b.user_id - a.user_id);
          setUsersWithStats(sortedData);
          setTotalCount(sortedData.length);
          setTotalPages(Math.ceil(sortedData.length / 10));
          setCurrentPage(1);
        } else {
          if (process.env.NODE_ENV === 'development') {
            console.error('UserManagement - Unexpected response format');
          }
        }
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('ユーザー統計の取得に失敗しました:', error);
      }
    } finally {
      setLoading(false);
    }
  }, [apiCall]);

  // 初期データ読み込み
  useEffect(() => {
    if (isAuthenticated && isAdmin()) {
      fetchUsersWithStats();
    }
  }, [isAuthenticated, isAdmin, fetchUsersWithStats]);

  // 検索実行
  const handleSearch = () => {
    setSearchTerm(searchInput);
    fetchUsersWithStats(searchInput);
  };

  // 検索リセット
  const handleResetSearch = () => {
    setSearchInput('');
    setSearchTerm('');
    fetchUsersWithStats('');
  };

  // ページ変更（フロントエンドページネーション）
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // ユーザー更新後のリフレッシュ
  const handleUserUpdated = () => {
    fetchUsersWithStats(searchTerm);
  };

  // アクセス権限チェック
  if (!isAuthenticated) {
    return (
      <PageWrapper title="アクセス拒否">
        <div className="text-center py-5">
          <div className="alert alert-warning">
            <h4>ログインが必要です</h4>
            <p>このページにアクセスするにはログインしてください。</p>
          </div>
        </div>
      </PageWrapper>
    );
  }

  if (!isAdmin()) {
    return (
      <PageWrapper title="アクセス拒否">
        <div className="text-center py-5">
          <div className="alert alert-danger">
            <h4>管理者権限が必要です</h4>
            <p>このページにアクセスするには管理者権限が必要です。</p>
          </div>
        </div>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper 
      title="ユーザー管理"
      actions={
        <a href="/mypage" className="btn btn-outline-secondary">
          <i className="fas fa-arrow-left me-2"></i>
          戻る
        </a>
      }
    >
      {/* 統計情報 */}
      {userStats && (
        <div className="row mb-4">
          <div className="col-12">
            <UserStatsCard stats={userStats} />
          </div>
        </div>
      )}

      {/* 検索機能 */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <div className="row align-items-end">
                <div className="col-md-8">
                  <label htmlFor="search" className="form-label">ユーザー検索</label>
                  <input
                    type="text"
                    id="search"
                    className="form-control"
                    placeholder="ユーザー名、メールアドレス、氏名で検索（日本語・英語対応）..."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  />
                </div>
                <div className="col-md-4">
                  <div className="d-flex gap-2">
                    <button 
                      className="btn btn-search"
                      onClick={handleSearch}
                    >
                      <i className="fas fa-search me-1"></i>
                      検索
                    </button>
                    <button 
                      className="btn btn-reset"
                      onClick={handleResetSearch}
                    >
                      <i className="fas fa-times me-1"></i>
                      リセット
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ユーザー一覧 */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5>
              ユーザー一覧 
              {searchTerm && (
                <small className="text-muted ms-2">
                  「{searchTerm}」の検索結果
                </small>
              )}
            </h5>
            <small className="text-muted">
              {totalCount}件中 {((currentPage - 1) * 10) + 1}-{Math.min(currentPage * 10, totalCount)}件を表示
            </small>
          </div>

          {loading ? (
            <LoadingSpinner />
          ) : usersWithStats.length === 0 ? (
            <div className="text-center py-5">
              <div className="alert alert-info">
                <i className="fas fa-info-circle me-2"></i>
                {searchTerm ? '検索条件に一致するユーザーが見つかりませんでした' : 'ユーザーが登録されていません'}
              </div>
            </div>
          ) : (
            <div className="user-cards-container">
              {/* フロントエンドページネーション: 10件ずつ表示 */}
              {usersWithStats
                .slice((currentPage - 1) * 10, currentPage * 10)
                .map((userWithStats) => (
                  <UserCard
                    key={userWithStats.user_id}
                    userWithStats={userWithStats}
                    onUserUpdated={handleUserUpdated}
                  />
                ))}
            </div>
          )}
        </div>
      </div>

      {/* ページネーション */}
      {totalPages > 1 && (
        <div className="row">
          <div className="col-12">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <span className="text-muted">
                {totalCount}件中 {((currentPage - 1) * 10) + 1}-{Math.min(currentPage * 10, totalCount)}件を表示
              </span>
              <span className="text-muted">
                ページ {currentPage} / {totalPages}
              </span>
            </div>
            <nav aria-label="ユーザーリストページネーション">
              <ul className="pagination justify-content-center">
                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                  <button
                    className="page-link"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    <i className="fas fa-chevron-left"></i>
                  </button>
                </li>

                {/* ページ番号 */}
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                  // 現在のページ周辺のページのみ表示
                  if (
                    page === 1 ||
                    page === totalPages ||
                    (page >= currentPage - 2 && page <= currentPage + 2)
                  ) {
                    return (
                      <li key={page} className={`page-item ${currentPage === page ? 'active' : ''}`}>
                        <button
                          className="page-link"
                          onClick={() => handlePageChange(page)}
                        >
                          {page}
                        </button>
                      </li>
                    );
                  } else if (page === currentPage - 3 || page === currentPage + 3) {
                    return (
                      <li key={page} className="page-item disabled">
                        <span className="page-link">...</span>
                      </li>
                    );
                  }
                  return null;
                })}

                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                  <button
                    className="page-link"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    <i className="fas fa-chevron-right"></i>
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      )}
    </PageWrapper>
  );
};

export default UserManagementPage; 